// Labor (Clock In/Out) — IC-5893. What every labor screen shares: the effective settings and the
// reference lists (GET /labor/settings), crews, employees, the period being looked at, the day open
// in the drawer, and a counter bumped after any change so every list refetches.
import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import {laborGet} from './laborApi';

const fetchLaborMeta = createAsyncThunk('labor/fetchMeta', async () => {
  const [settings, crews] = await Promise.all([laborGet('settings'), laborGet('crews', {includeArchived: true})]);
  const meta = settings.data;
  let employees = [];
  // Operators are not allowed the employee list; they only ever see themselves
  const role = meta.me.role;
  if (meta.me.laborRole !== 'operator' || role === 'customer_admin' || role === 'ic_admin') {
    employees = (await laborGet('employees')).data;
  }
  return {meta, crews: crews.data, employees};
});

const laborSlice = createSlice({
  name: 'labor',
  initialState: {
    meta: null, // {settings, stored, defaults, reasons, flags, rulesVersion, me}
    crews: [],
    employees: [],
    metaStatus: 'idle', // idle | loading | ready | error
    metaError: null,
    period: null, // {startKey, endKey}
    openDayId: null,
    refreshTick: 0,
  },
  reducers: {
    setLaborPeriod(state, action) {
      state.period = action.payload;
    },
    openLaborDay(state, action) {
      state.openDayId = action.payload ? String(action.payload) : null;
    },
    laborChanged(state) {
      state.refreshTick += 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLaborMeta.pending, (state) => {
        state.metaStatus = state.meta ? 'ready' : 'loading';
      })
      .addCase(fetchLaborMeta.fulfilled, (state, action) => {
        state.meta = action.payload.meta;
        state.crews = action.payload.crews;
        state.employees = action.payload.employees;
        state.metaStatus = 'ready';
        state.metaError = null;
      })
      .addCase(fetchLaborMeta.rejected, (state, action) => {
        state.metaStatus = state.meta ? 'ready' : 'error';
        state.metaError = action.error.message;
      });
  },
});

// Is the viewer a supervisor, payroll (or an admin acting as payroll), or an operator?
function viewerRole(meta) {
  if (!meta) return 'operator';
  return meta.me.laborRole || 'operator';
}

function canExport(meta) {
  if (!meta) return false;
  return meta.me.laborRole === 'payroll' || meta.me.role === 'customer_admin' || meta.me.role === 'ic_admin';
}

export const {setLaborPeriod, openLaborDay, laborChanged} = laborSlice.actions;
export {fetchLaborMeta, viewerRole, canExport};
export default laborSlice.reducer;
